import { Field } from "@base-ui/react/field";
import type { ComponentPropsWithRef, ReactNode } from "react";
import { cx } from "../presentation.tsx";
import { Icon } from "../icons.tsx";

export interface TextFieldProps extends Omit<ComponentPropsWithRef<"input">, "size"> {
  label: ReactNode;
  /** Help text under the input, linked with aria-describedby. */
  hint?: ReactNode;
  /** Error message; marks the field invalid and replaces nothing (the hint stays). */
  error?: ReactNode;
  /** Visually hide the label (it stays the accessible name). */
  hideLabel?: boolean;
}

export function TextField({ label, hint, error, hideLabel, className, disabled, required, ...rest }: TextFieldProps) {
  const invalid = Boolean(error);
  return (
    <Field.Root className={cx("pmu-field", className)} invalid={invalid} disabled={disabled}>
      <Field.Label className={cx("pmu-field__label", hideLabel && "pmu-sr-only")}>
        {label}
        {required && <span className="pmu-field__required" aria-hidden="true"> *</span>}
      </Field.Label>
      <Field.Control {...rest} required={required} className="pmu-input" aria-invalid={invalid || undefined} />
      {hint && <Field.Description className="pmu-field__hint">{hint}</Field.Description>}
      {invalid && (
        <Field.Error className="pmu-field__error" match={true}>
          <Icon name="warning" />
          {error}
        </Field.Error>
      )}
    </Field.Root>
  );
}
